import { ExternalLink, UserIcon } from 'lucide-react'
import { type ComponentProps } from 'react'

import DisplayTooltipOnHover from 'components/reusable/DisplayTooltipOnHover'
import { adminRecordInspector } from 'rails_routes'
import { Badge } from 'slab'
import { getFormattedDate } from 'utils'
import { type BrokersBrokerMemberDenormalized } from 'utils/web_api.schemas'

function getBadgeTypeInfo (member: BrokersBrokerMemberDenormalized): {
  type: ComponentProps<typeof Badge>['type']
  text: string
  tooltipText: string
} {
  const {
    termination_config: terminationConfig,
    employment_status: employmentStatus,
  } = member

  switch (employmentStatus) {
    case 'active':
      if (terminationConfig) {
        return getOffboardingScheduledBadgeTypeInfo(
          'active',
          terminationConfig.effective_date as string
        )
      } else {
        return {
          type: 'success',
          text: 'Active',
          tooltipText: 'This member is currently an active employee',
        }
      }
    case 'pending_deactivation':
      if (terminationConfig) {
        return getOffboardingScheduledBadgeTypeInfo(
          'payroll deactivated',
          terminationConfig.effective_date as string
        )
      } else {
        return {
          type: 'warning',
          text: 'Payroll Deactivated',
          tooltipText:
            'This member has been deactivated in payroll. The employer still need to confirm the offboarding before it takes effect.',
        }
      }
    case 'inactive':
      return {
        type: 'critical',
        text: 'Offboarded',
        tooltipText: `This member is no longer an active employee. ${
          terminationConfig &&
          `Effective Date: ${getFormattedDate(
            terminationConfig.effective_date as string,
            { timeZone: 'UTC' }
          )},
          Reported: ${getFormattedDate(terminationConfig.created_at as string)}`
        }`,
      }
    default:
      return {
        type: 'critical',
        text: 'Unknown',
        tooltipText: 'Unknown employment status',
      }
  }
}

function getOffboardingScheduledBadgeTypeInfo (
  employmentStatus: string,
  effectiveDate: string
): {
    type: ComponentProps<typeof Badge>['type']
    text: string
    tooltipText: string
  } {
  return {
    type: 'warning',
    text: 'Offboarding Scheduled',
    tooltipText: `This employee is currently ${employmentStatus} and has been scheduled to be terminated EOD ${getFormattedDate(
      effectiveDate,
      { timeZone: 'UTC' }
    )}`,
  }
}

const EmploymentStatusBadge = ({
  member,
}: {
  member: BrokersBrokerMemberDenormalized
}) => {
  const { type, text, tooltipText } = getBadgeTypeInfo(member)

  const terminationConfig = member.termination_config
  return (
    <DisplayTooltipOnHover text={tooltipText} asChild>
      {terminationConfig
        ? (
          <Badge
            leftIcon={UserIcon}
            type={type}
            actionIcon={ExternalLink}
            actionLabel='View TerminationConfig'
            onActionClick={() =>
              window.open(adminRecordInspector.show({ id: terminationConfig.id }))}
          >
            {text}
          </Badge>
          )
        : (
          <Badge type={type} leftIcon={UserIcon}>{text}</Badge>
          )}
    </DisplayTooltipOnHover>
  )
}

export default EmploymentStatusBadge
